import dayjs from "utils/dayjs";
import { titleCase } from "title-case";
import CardType from "enums/CardType";
import { surnameFirstName } from "utils/fullName";
import { bindingLimit, type LimitPeriod } from "utils/cards/cardLimit";
import type { UserAvatarUser } from "components/common/users/UserAvatar";

export interface CardPolicyLimits {
  transaction_limit?: number | null;
  daily_limit?: number | null;
  weekly_limit?: number | null;
  monthly_limit?: number | null;
  total_limit?: number | null;
}

export interface CardSpendingSummary {
  dailySpent?: number | null;
  weeklySpent?: number | null;
  monthlySpent?: number | null;
  yearlySpent?: number | null;
  totalSpent?: number | null;
}

export type RestrictionType =
  "merchant" | "category" | "blocked" | "blockedCategory";

export interface RestrictionEntry {
  name: string;
  type: RestrictionType;
}

export interface CardRestrictionPolicy {
  merchants?: ({ name?: string | null } | null)[] | null;
  allowed_categories?: ({ name?: string | null } | null)[] | null;
  blocked_merchants?: ({ name?: string | null } | null)[] | null;
  blocked_categories?: ({ name?: string | null } | null)[] | null;
}

export const EMPTY_CELL = "-";

export const formatPolicyDate = (
  raw?: string | number | null,
): string | null => {
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("MMM YYYY") : null;
};

const LIMIT_TYPE_LABELS: Record<LimitPeriod, string> = {
  transaction: "Transaction Limit",
  daily: "Daily Limit",
  weekly: "Weekly Limit",
  monthly: "Monthly Limit",
  total: "Total Limit",
};

/** The cards tables' reading of `bindingLimit`; an Infinity limit means the card has none. */
export const cardSpendingPower = (
  policy?: CardPolicyLimits | null,
  summary?: CardSpendingSummary | null,
): { limit: number; spent: number; limitType: string } => {
  const binding = bindingLimit(policy, summary);
  if (!binding) {
    return {
      limitType: LIMIT_TYPE_LABELS.transaction,
      limit: Infinity,
      spent: 0,
    };
  }
  return {
    limitType: LIMIT_TYPE_LABELS[binding.period],
    limit: binding.limit,
    spent: binding.spent,
  };
};

const named = (list?: ({ name?: string | null } | null)[] | null) =>
  (list ?? []).filter(
    (entry): entry is { name: string } => !!entry && !!entry.name,
  );

export const cardRestrictionList = (
  policy?: CardRestrictionPolicy | null,
): RestrictionEntry[] => {
  const merchants = named(policy?.merchants);
  const allowed: RestrictionEntry[] = merchants.length
    ? merchants.map((entry) => ({
        name: titleCase(entry.name.toLowerCase()),
        type: "merchant",
      }))
    : named(policy?.allowed_categories).map((entry) => ({
        name: entry.name,
        type: "category",
      }));

  const blocked: RestrictionEntry[] = [
    ...named(policy?.blocked_merchants).map((entry) => ({
      name: titleCase(entry.name.toLowerCase()) as string,
      type: "blocked" as const,
    })),
    ...named(policy?.blocked_categories).map((entry) => ({
      name: entry.name,
      type: "blockedCategory" as const,
    })),
  ];

  return [...allowed, ...blocked];
};

export const hasPendingRequest = (
  pendingRequest?: { id?: string | null } | null,
): boolean => !!pendingRequest?.id;

// The terms govern cards Outpave issues; an external card is the customer's own.
export const isCardPendingTerms = (
  card?: {
    type?: string | null;
    user?: { acceptedTerms?: boolean | null } | null;
  } | null,
): boolean =>
  !!card &&
  card.type !== CardType.EXTERNAL &&
  !!card.user &&
  !card.user.acceptedTerms;

// Merchant and category lists arrive in arbitrary order, so compare by id set.
export const sameByIds = (
  a?: ({ id?: string } | null)[] | null,
  b?: ({ id?: string } | null)[] | null,
) => {
  const ids = (list?: ({ id?: string } | null)[] | null) =>
    (list ?? []).map((entry) => String(entry?.id)).sort();
  return JSON.stringify(ids(a)) === JSON.stringify(ids(b));
};

export interface CardRowNode {
  consoleProgram?: { id: string; name: string; issuer?: string } | null;
  id: string;
  cardNumber: string;
  nickname?: string | null;
  status: string;
  type: string;
  user: {
    firstName?: string | null;
    lastName?: string | null;
    profilePicUrl?: string | null;
    acceptedTerms?: boolean | null;
  };
  activePolicy?:
    | (CardPolicyLimits &
        CardRestrictionPolicy & {
          card_expiration_date?: string | number | null;
          card_cancellation_date?: string | number | null;
        })
    | null;
  spendingSummary?: CardSpendingSummary | null;
  pendingRequest?: {
    id?: string | null;
    requestType?: string | null;
    requestData?: {
      card_expiration_date?: string | number | null;
      card_cancellation_date?: string | number | null;
    } | null;
  } | null;
}

export interface CardRowData {
  id: string;
  userName: string;
  user: UserAvatarUser;
  cardNumber: string;
  cardStatus: string;
  type: string;
  expiration: string | null;
  cancelation: string | null;
  limitType: string;
  limit: number;
  spent: number;
  combinedCategoryAndMerchant: RestrictionEntry[];
  isPendingRequest: boolean;
  pendingRequestType?: string | null;
  pendingTerms: boolean;
}

export const cardRowData = (node: CardRowNode): CardRowData => {
  const { limit, spent, limitType } = cardSpendingPower(
    node.activePolicy,
    node.spendingSummary,
  );

  return {
    id: node.id,
    userName: surnameFirstName(node.user),
    user: node.user,
    cardNumber: node.nickname
      ? `${node.cardNumber} - ${node.nickname}`
      : node.cardNumber,
    cardStatus: node.status,
    type: node.type,
    expiration: formatPolicyDate(
      node.activePolicy?.card_expiration_date ??
        node.pendingRequest?.requestData?.card_expiration_date,
    ),
    cancelation: formatPolicyDate(
      node.activePolicy?.card_cancellation_date ??
        node.pendingRequest?.requestData?.card_cancellation_date,
    ),
    limitType,
    limit,
    spent,
    combinedCategoryAndMerchant: cardRestrictionList(node.activePolicy),
    isPendingRequest: hasPendingRequest(node.pendingRequest),
    pendingRequestType: node.pendingRequest?.requestType,
    pendingTerms: isCardPendingTerms(node),
  };
};
